/** @jsx jsx */
import { Button, Card, Heading, Inline, jsx, Stack } from '@front10/base-elements';
import { Icon } from '../../../packages/base-elements/Icon/Icon';
import { useFullViewport } from '@front10/utils';
import { DialogContent } from '@reach/dialog';
import { RemoveScroll } from 'react-remove-scroll';
import { PropsWithChildren, useRef, useEffect } from 'react';
import { useTranslation } from '@front10/utils';
import FocusLock from 'react-focus-lock';
import { OverlayContainer } from '@react-aria/overlays';

interface MobileFilterDialogProps {
  isVisible?: boolean;
  label: string;
  title: string;
  onDismiss: () => void;
}

const KEY_ESC = 27;

export function PopupDialog(props: PropsWithChildren<MobileFilterDialogProps>) {
  const isVisible = props.isVisible ?? true;
  const label = props.label ?? 'Filters';
  const translate = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    buttonRef?.current?.focus();
  }, []);

  useFullViewport(isVisible);

  return (
    <FocusLock autoFocus returnFocus>
      <RemoveScroll allowPinchZoom={true} enabled={true}>
        <OverlayContainer>
          <div
            sx={{
              zIndex: 10,
              display: isVisible ? undefined : 'none',
              position: 'fixed',
              top: 0,
              left: 0,
              maxHeight: '100vh',
              overflow: 'auto',
              variant: 'layout.popup.overlay'
            }}
            onKeyDown={(e) => {
              if (e.keyCode === KEY_ESC) {
                props.onDismiss();
              }
            }}
          >
            <DialogContent
              aria-label={label}
              sx={{
                width: '100vw',
                padding: 0,
                margin: 0,
                overflow: 'hidden',
                bg: 'rgba(0, 0, 0, 80%)',
                display: 'flex',
                justifyContent: 'center',
                variant: 'layout.popup.dialogContent'
              }}
            >
              <Stack
                sx={{
                  width: '100%',
                  maxWidth: 'sm',
                  minHeight: '100vh',
                  maxHeight: '100vh',
                  overflow: 'hidden'
                }}
                alignY="center"
              >
                <Card sx={{ overflow: 'auto' }}>
                  <Inline align="right" pt="1" pr="1">
                    <Button
                      variant="icon"
                      onClick={props.onDismiss}
                      aria-label={translate('cmp-booking-popup-close-button', 'close')}
                      ref={buttonRef}
                    >
                      <Icon icon="close" size={32} sx={{ stroke: 'primary', strokeWidth: '70' }} />
                    </Button>
                  </Inline>
                  <Stack gap="4" pl="3" pr="3" pt="1" pb="4">
                    <Heading color="text">{props.title}</Heading>
                    {props.children}
                  </Stack>
                </Card>
              </Stack>
            </DialogContent>
          </div>
        </OverlayContainer>
      </RemoveScroll>
    </FocusLock>
  );
}
